<template>
  <v-overlay z-index="9999" :value="value" @input="$emit('input', $event)">
    <div class="cookie-consent-dialog">
      <header class="d-flex align-center justify-space-between">
        <div class="subtitle-1">{{ $t('cookie-consent.title') }}</div>
        <TopBarLocaleChanger />
      </header>
  
      <main>
        <p class="body-1 mb-0">
          {{ $t('cookie-consent.text') }}
          <a target="_blank" href="https://www.byggbranschensyrkesnamnd.se/wp-content/uploads/2026/10/Cookiepolicy-Mina-Sidor-och-Registret.pdf">{{ $t('cookie-consent.links.read_more.label') }}</a>
        </p>
      </main>
  
      <footer>
        <div class="d-flex align-center justify-end cookie-consent-dialog__actions">
          <v-btn block class="cookie-consent-dialog__actions-btn" @click="save(false)">{{ $t('cookie-consent.actions.decline') }}</v-btn>
          <v-btn block class="cookie-consent-dialog__actions-btn" @click="save(true)">{{ $t('cookie-consent.actions.accept') }}</v-btn>
        </div>
      </footer>
    </div>
  </v-overlay>
</template>

<script>
import { getConsent, setConsent, loadConsentScript, unloadConsentScript } from '@/utils/consent'
import { mapGetters } from 'vuex';
import API from '@/services/api'
import { mapActions } from 'vuex';
import TopBarLocaleChanger from '@/components/Common/Login/TopBarLocaleChanger.vue';

export default {
  name: 'CookieConsentDialog',

  components: {
    TopBarLocaleChanger
  },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      allowStats: getConsent() === 'accepted',
    }
  },

  computed: {
    ...mapGetters('currentuser', ['currentUser']),
  },

  watch: {
    // Sync the switch with the stored choice every time the dialog opens
    value(open) {
      if (open) {
        this.allowStats = getConsent() === 'accepted'
      }
    },

    currentUser: {
      immediate: true,
      handler(userData) {
        if (!userData) return

        const consent = getConsent()

        if (consent !== null) return

        if (userData.allowAnalytics !== null) {
          return
        }
      }
    }
  },

  methods: {
    ...mapActions("currentuser", ["updateCurrentUserData"]),

    save(allowStats) {
      setConsent(allowStats ? 'accepted' : 'rejected')

      if (allowStats) {
        loadConsentScript()
      } else {
        unloadConsentScript()
      }

      const update = this.currentUser
        ? API.updatePerson({
            data: {
              ...this.currentUser,
              allowAnalytics: allowStats
            }
          }).then(res => {
            return this.updateCurrentUserData(res.data)
          })
        : Promise.resolve()

      update
        .then(() => {
          if (!allowStats) {
            window.location.reload()
            return
          }

          this.$emit('input', false)
        })
        .catch(() => {
          this.$events.fire('showError', this.$t('errors.save-failure'))
        })
    }
  },
}
</script>

<style lang="scss" scoped>
.cookie-consent-dialog {
  position: fixed;
  width: 100%;
  z-index: 9999;
  max-width: 880px;
  background-color: #FFF;
  padding: 32px;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  left: 50%;
  bottom: 0;
  color: #000000de;
  transform: translate(-50%, 0);
  animation: cookie-consent-dialog-enter 300ms ease-out;

  &__actions {
    gap: 8px;
  }

  @media (max-width: 600px) {
    &__actions {
      flex-wrap: wrap;
      gap: 18px;

      &-btn {
        width: 100%;
      }
    }
  }

  @media (max-width: 900px) {
    border-radius: 16px 16px 0 0;
  }

  @media (min-width: 901px) {
    top: 50%;
    bottom: auto;
    transform: translate(-50%, -50%);
    animation-name: cookie-consent-dialog-enter-centered;
  }
}

@keyframes cookie-consent-dialog-enter {
  from {
    opacity: 0;
    transform: translate(-50%, 20px);
  }

  to {
    opacity: 1;
    transform: translate(-50%, 0);
  }
}

@keyframes cookie-consent-dialog-enter-centered {
  from {
    opacity: 0;
    transform: translate(-50%, calc(-50% + 20px));
  }

  to {
    opacity: 1;
    transform: translate(-50%, -50%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .cookie-consent-dialog {
    animation: none;
  }
}
</style>